import {
  Box,
  CircularProgress,
  ListItem,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { PoliciesService } from 'services';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { Utils } from 'shared/helpers';
import useBroadCast from 'shared/hooks/useBroadCast';
import { BROADCAST_MESSAGES } from 'shared/Constants';

interface IReviewHistoryProps {
  policyId: string;
}

export interface ReviewedBy {
  _id: string;
  email: string;
  name: string;
}

export interface IReview {
  _id: string;
  policyId: string;
  notes: string;
  nextReviewDate: string;
  ReviewedBy: ReviewedBy;
  organizationId: string;
  isActive: boolean;
  ReviewedDate: string;
  __v: number;
  previousReviewedDate: any;
}

const ReviewHistory: React.FC<IReviewHistoryProps> = (props) => {
  const { policyId } = props;
  const [isLoadingRecords, setIsLoadingRecords] = useState(false);
  const [records, setRecords] = useState<IReview[]>([]);

  // API Calls
  const loadHistory = (): void => {
    setIsLoadingRecords(true);
    PoliciesService.reviewHistory(policyId)
      .then((response) => {
        const { data } = response.data;
        /* eslint no-underscore-dangle: 0 */
        setRecords(data.map((d: any) => ({ ...d, id: d._id })));
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setIsLoadingRecords(false);
      });
  };

  // Hooks
  useEffect(() => {
    loadHistory();
  }, []);

  useBroadCast({
    eventName: BROADCAST_MESSAGES.RELOAD_ASSESSMENTS,
    onBroadCast: () => {
      loadHistory();
    },
  });

  // UI Elements
  return (
    <Box className="app-plain-card mt-4">
      <Box className="title">
        <Typography variant="h4">Review History</Typography>
        <Typography className="text-gray">
          A record of all reviews made to this policy
        </Typography>
      </Box>

      <Box className="content">
        {isLoadingRecords && (
          <Box className="empty-content">
            <CircularProgress />
          </Box>
        )}

        {!isLoadingRecords && (
          <>
            {records.map((review) => (
              <ListItem className="app-box-border mt-4">
                <Stack sx={{ width: '100%' }}>
                  <Stack
                    className="mb-5 my-2"
                    alignItems="center"
                    direction="row"
                    justifyContent="space-between"
                    spacing={2}
                  >
                    <Stack
                      alignItems="center"
                      direction="row"
                      justifyContent="space-between"
                      spacing={2}
                    >
                      <PersonOutlineIcon />
                      <Typography className="f18 fw-6">
                        {review.ReviewedBy?.name}
                      </Typography>
                    </Stack>
                    <Stack alignItems="center" direction="row" spacing={2}>
                      <CalendarMonthIcon />
                      <Typography variant="body1" className="text-gray">
                        Reviewed: {Utils.formatDateTime(review.ReviewedDate)}
                      </Typography>
                    </Stack>
                  </Stack>
                  <Stack direction="row" spacing={10}>
                    {review.previousReviewedDate && (
                      <Stack>
                        <Typography variant="body1" className="mb-2">
                          Previous Review Date:
                        </Typography>
                        <Typography variant="body1" className="mb-2">
                          {Utils.formatDateTime(review.previousReviewedDate)}
                        </Typography>
                      </Stack>
                    )}
                    <Stack>
                      <Typography variant="body1" className="mb-2">
                        Next Review Date:
                      </Typography>
                      <Typography variant="body1" className="mb-2">
                        {Utils.formatDateTime(review.nextReviewDate)}
                      </Typography>
                    </Stack>
                  </Stack>
                  {review.notes && (
                    <Typography className="text-gray">
                      Notes: {review.notes}
                    </Typography>
                  )}
                </Stack>
              </ListItem>
            ))}

            {records.length === 0 && (
              <Box className="empty-content">
                <Typography className="f18">No reviews found</Typography>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default ReviewHistory;
